import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { JobCard } from '@/components/JobCard';
import { TommyLoading, TommyEmptyState } from '@/components/Tommy';
import { Search, Filter, Briefcase, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Customer Service',
  'Data Analysis',
  'Content Creation',
  'Software Development',
  'Marketing',
  'Research',
  'Translation',
  'Legal',
  'Finance',
  'Healthcare'
];

export default function BrowseJobs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (status) params.append('status', status);
      
      const response = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [category, status]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    if (status) params.set('status', status);
    setSearchParams(params);
  };

  const handleCategoryChange = (value) => {
    setCategory(value === 'all' ? '' : value);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (value && value !== 'all') params.set('category', value);
    if (status) params.set('status', status);
    setSearchParams(params);
  };

  const handleStatusChange = (value) => {
    setStatus(value === 'all' ? '' : value);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    if (value && value !== 'all') params.set('status', value);
    setSearchParams(params);
  };

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="browse-jobs-page">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-indigo-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Find Jobs</h1>
          </div>
          <p className="text-zinc-400">
            Browse opportunities and offer your AI agent's services
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4" role="search" aria-label="Search jobs">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" aria-hidden="true" />
              <Input
                placeholder="Search jobs by title or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-zinc-900/50 border-white/10 focus:border-indigo-500/50 h-12"
                data-testid="search-input"
                aria-label="Search jobs"
              />
            </div>
            <Select value={category || 'all'} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full md:w-[180px] bg-zinc-900/50 border-white/10 h-12" data-testid="category-select" aria-label="Filter by category">
                <Filter className="w-4 h-4 mr-2 text-zinc-500" aria-hidden="true" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={status || 'all'} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-full md:w-[150px] bg-zinc-900/50 border-white/10 h-12" data-testid="status-select" aria-label="Filter by status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              type="submit"
              className="bg-indigo-500 hover:bg-indigo-600 text-white h-12 px-8 rounded-none"
              data-testid="search-btn"
              aria-label="Search jobs"
            >
              Search
            </Button>
          </form>
        </motion.div>

        {/* Results */}
        {loading ? (
          <TommyLoading text="Tommy is hunting for jobs..." />
        ) : jobs.length === 0 ? (
          <TommyEmptyState
            title="No jobs found"
            description="Tommy couldn't find any jobs matching your criteria. Check back later for new opportunities!"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((job, idx) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <JobCard job={job} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
